const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const CONTENT_DISPOSITION_FILENAME_RE = /filename="?([^";]+)"?/i;

export function filenameFromContentDisposition(header: string | null): string | undefined {
  const match = header ? CONTENT_DISPOSITION_FILENAME_RE.exec(header) : undefined;
  return match?.[1];
}

export interface ApiError {
  status: number;
  code: string;
  message: string;
  retryable: boolean;
}

export function isRetryableApiStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

export async function parseApiError(response: Response, fallback: string): Promise<ApiError> {
  const body = await response.json().catch(() => null) as { code?: string; message?: string } | null;
  return {
    status: response.status,
    code: body?.code ?? `HTTP_${response.status}`,
    message: body?.message || response.statusText || fallback,
    retryable: isRetryableApiStatus(response.status),
  };
}

/**
 * Coerce an unknown thrown value into an ApiError. A request that never gets a
 * response (offline, DNS failure, abort) throws a bare TypeError carrying no
 * `status`; callers that only forward errors with one leave the UI waiting on a
 * result that never comes.
 */
export function normalizeApiError(error: unknown, fallback = 'Request failed'): ApiError {
  if (error && typeof error === 'object' && 'status' in error) return error as ApiError;
  return {
    status: 0,
    code: 'NETWORK_ERROR',
    message: error instanceof Error && error.message ? error.message : fallback,
    retryable: true,
  };
}

export interface ListQueryParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  assignedAccountId?: string;
  /** Repeated ?tagIds=1&tagIds=2 — matches any of them (OR), server-side. */
  tagIds?: string[];
  /** NA-437: Lesson list only — excludes lessons with no Emtrain/base content row. */
  excludeAccountOnly?: boolean;
  /** NA-437: Lesson list only — excludes this lesson id from the results. */
  excludeLessonId?: string;
}

export function buildListSearchParams(params: ListQueryParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.pageSize !== undefined) sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.order) sp.set('order', params.order);
  if (params.search !== undefined && params.search !== '') sp.set('search', params.search);
  if (params.assignedAccountId && /^\d+$/.test(params.assignedAccountId)) {
    sp.set('assignedAccountId', params.assignedAccountId);
  }
  for (const tagId of params.tagIds ?? []) {
    if (/^\d+$/.test(tagId)) sp.append('tagIds', tagId);
  }
  if (params.excludeAccountOnly) sp.set('excludeAccountOnly', 'true');
  if (params.excludeLessonId) sp.set('excludeLessonId', params.excludeLessonId);
  return sp;
}

export async function handleApiResponse<T>(response: Response, fallback: string): Promise<T> {
  if (response.ok) return response.json() as Promise<T>;
  const body = await response.json().catch(() => ({ message: fallback }));
  throw new Error(typeof body.message === 'string' ? body.message : fallback);
}

type JsonHeaderMode = 'always' | 'withBody' | 'never';

export interface AuthRetryOptions {
  json?: JsonHeaderMode;
  retryStatuses?: readonly number[];
}

export function createJsonHeaders(existingHeaders?: HeadersInit): Headers {
  const headers = new Headers(existingHeaders);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return headers;
}

/**
 * Dispatched on window when an authenticated request fails and the token
 * refresh also fails — the session is definitively gone. AuthProvider listens
 * for this and clears the user, which sends ProtectedRoute back to the login
 * page instead of leaving the app rendering with no data.
 */
export const SESSION_EXPIRED_EVENT = 'auth:session-expired';

function notifySessionExpired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

let inFlightRefreshPromise: Promise<boolean> | null = null;
let refreshGeneration = 0;

/**
 * The refresh promise is SHARED — every 401 retry joins it, so it must be
 * time-bounded itself: a hung refresh would otherwise stall every joining
 * request indefinitely (outer wrappers can only release their own caller).
 * A timeout rejects (AbortError) rather than resolving false, so a mere
 * network stall never fires the session-expired flow.
 */
const REFRESH_TIMEOUT_MS = 10_000;

async function refreshAccessToken(): Promise<boolean> {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: createJsonHeaders(),
    signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
  });

  return response.ok;
}

async function refreshAccessTokenOnce(): Promise<boolean> {
  if (!inFlightRefreshPromise) {
    const generation = ++refreshGeneration;
    inFlightRefreshPromise = refreshAccessToken().finally(() => {
      // Only the CURRENT attempt clears the slot — a late settlement from a
      // superseded attempt must never clobber a newer in-flight refresh.
      if (generation === refreshGeneration) inFlightRefreshPromise = null;
    });
  }
  return inFlightRefreshPromise;
}

function shouldApplyJsonHeaders(init: RequestInit | undefined, mode: JsonHeaderMode): boolean {
  if (mode === 'always') return true;
  if (mode === 'never') return false;
  return init?.body !== undefined && init.body !== null;
}

function createAuthRequestInit(init?: RequestInit, options?: AuthRetryOptions): RequestInit {
  const jsonMode = options?.json ?? 'always';
  const headers = shouldApplyJsonHeaders(init, jsonMode)
    ? createJsonHeaders(init?.headers)
    : init?.headers;

  return {
    ...init,
    credentials: 'include',
    ...(headers ? { headers } : {}),
  };
}

export async function fetchWithAuthRetry(
  url: string,
  init?: RequestInit,
  options?: AuthRetryOptions
): Promise<Response> {
  const retryStatuses = options?.retryStatuses ?? [401, 403];
  const response = await fetch(url, createAuthRequestInit(init, options));

  if (!retryStatuses.includes(response.status)) {
    return response;
  }

  const didRefresh = await refreshAccessTokenOnce();
  if (!didRefresh) {
    notifySessionExpired();
    return response;
  }

  return fetch(url, createAuthRequestInit(init, options));
}
